import basemapService from "../../../../../services/basemap/basemapService";
import { LocalStorageKey } from "../../../../../constants/localStorageKeys";
import { BASEMAPS } from "../../utils/consts";
import type { MapLayer } from "../../layers/types";
import { changeBasemap, setBasemapList } from "../slices/mapSlice";

/**
 * Load the basemap list from the API, falling back to the hardcoded BASEMAPS
 * when the request fails or returns nothing. Restores the previously selected
 * basemap from localStorage (by id); on first visit selects the first item.
 * The chosen basemap is persisted so a reload keeps it.
 *
 * API response: { data: [{ id, is_active, value: <MapLayer> }, ...] }
 * — each item's `value` is the MapLayer style (same shape as BASEMAPS).
 */
export const loadBasemaps = () => async (dispatch: any, getState: any) =>
{
   // Already loaded (e.g. a second UniversalMap mounting inside a modal) → do
   // nothing. Re-dispatching changeBasemap here would change selectedBasemap's
   // reference and make every mounted map re-apply its style (visible flicker).
   const mapState = getState()?.map;
   if (mapState?.basemaps?.length > 0 && mapState?.selectedBasemap) return;

   // Read any previously-saved selection up front.
   let saved: any;
   try
   {
      const savedRaw = localStorage.getItem(LocalStorageKey.BASEMAP_SELECTED);
      saved = savedRaw ? JSON.parse(savedRaw) : undefined;
   } catch (error) { }

   // Seed a basemap SYNCHRONOUSLY so the map has a style before map plugins
   // (AOI/Measure) activate and call getSource. Use the saved one if valid,
   // otherwise the first hardcoded basemap.
   dispatch(setBasemapList(BASEMAPS));
   dispatch(changeBasemap(saved ?? BASEMAPS?.[0]));

   // Fetch the real list from the API; on failure keep the hardcoded fallback.
   let list: MapLayer[] = BASEMAPS;
   try
   {
      const res = await basemapService.GetbaseMap();
      const raw = res?.data?.data;
      if (Array.isArray(raw))
      {
         const mapped = raw
            .map((item: any) => item?.value)
            .filter((v: any) => v && v?.visibility !== false)
            // API gives `title`; the panel + maplibre adapter expect `name`.
            .map((v: any) => ({ ...v, name: v?.name ?? v?.title ?? v?.id }));
         if (mapped.length) list = mapped;
      }
   } catch (error)
   {
      list = BASEMAPS;
   }

   dispatch(setBasemapList(list));

   // Restore the previously selected basemap (matched by id); otherwise first.
   const selected: MapLayer | undefined =
      (saved && list?.find((b: any) => b?.id === saved?.id)) || list?.[0];

   dispatch(changeBasemap(selected));
   if (selected)
   {
      localStorage.setItem(
         LocalStorageKey.BASEMAP_SELECTED,
         JSON.stringify(selected),
      );
   }
};
